/**
 * useExternalDatasets — Inteligência de Dados (Fase 2)
 * -----------------------------------------------------------------------------
 * Hook único de acesso aos DATASETS EXTERNOS (dados importados de sistemas de
 * terceiros) e ao histórico de sincronizações.
 *
 * Regras de arquitetura:
 *   - 100% via RPC SECURITY DEFINER (nada de SELECT direto em tabelas).
 *   - Nenhuma chamada a provider parte do navegador: importar apenas enfileira
 *     a execução (worker PGMQ `external-import-worker` faz o trabalho pesado).
 *   - Limpar dados é instantâneo (troca de geração); a remoção física roda em
 *     segundo plano no worker de cleanup.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (Fase 2 - Dados Externos).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('ExternalDatasets');

// =============================================================================
// TIPOS
// =============================================================================

/** Dataset externo configurado pelo tenant */
export interface ExternalDataset {
  id: string;
  name: string;
  description: string | null;
  connection_id: string;
  connection_name: string;
  provider_slug: string;
  provider_name: string;
  operation_code: string;
  import_mode: string;
  identity_strategy: string;
  identity_fields: string[];
  params: Record<string, unknown> | null;
  record_count: number;
  status: string;
  last_import_at: string | null;
  fields_count: number;
  /** Preenchido quando existe importação em andamento (permite cancelar) */
  running_run_id: string | null;
  created_at: string;
}

/** Campo descoberto dentro do JSON importado */
export interface ExternalDatasetField {
  id: string;
  json_path: string;
  label: string;
  data_type: string;
  is_indexed: boolean;
  is_visible: boolean;
  sort_order: number;
  format: string | null;
}

/** Execução (run) de importação */
export interface ExternalImportRun {
  id: string;
  dataset_id: string;
  dataset_name: string;
  status: string;
  mode: string;
  page: number;
  total_fetched: number;
  inserted: number;
  updated: number;
  unchanged: number;
  failed: number;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  total_count: number;
}

/** Payload de criação/edição de dataset */
export interface SaveDatasetInput {
  datasetId?: string | null;
  name: string;
  connectionId: string;
  operationCode: string;
  description?: string | null;
  importMode?: string;
  identityStrategy?: string;
  identityFields?: string[];
  params?: Record<string, unknown>;
}

// =============================================================================
// HOOK PRINCIPAL — LISTAGEM E AÇÕES
// =============================================================================

export function useExternalDatasets(options: { search?: string } = {}) {
  const { search = '' } = options;
  const queryClient = useQueryClient();

  /**
   * Datasets do tenant. Enquanto houver importação rodando, refaz a busca a
   * cada 10s (polling leve — evita realtime desnecessário nesta tela).
   */
  const datasetsQuery = useQuery({
    queryKey: ['external-datasets', search],
    staleTime: 15 * 1000,
    queryFn: async (): Promise<ExternalDataset[]> => {
      const { data, error } = await supabase.rpc('list_external_datasets', {
        p_search: search.trim() || null,
      });
      if (error) throw error;
      return (data ?? []) as unknown as ExternalDataset[];
    },
    refetchInterval: (query) => {
      const rows = (query.state.data ?? []) as ExternalDataset[];
      return rows.some((d) => d.running_run_id) ? 10_000 : false;
    },
  });

  /** Invalida datasets e histórico (toda ação mexe nos dois) */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['external-datasets'] });
    queryClient.invalidateQueries({ queryKey: ['external-import-runs'] });
  };

  /** Cria ou atualiza um dataset */
  const saveDataset = useMutation({
    mutationFn: async (input: SaveDatasetInput): Promise<string> => {
      const { data, error } = await supabase.rpc('upsert_external_dataset', {
        p_name: input.name,
        p_connection_id: input.connectionId,
        p_operation_code: input.operationCode,
        p_dataset_id: input.datasetId ?? null,
        p_description: input.description ?? null,
        p_import_mode: input.importMode ?? 'FULL_REFRESH',
        p_identity_strategy: input.identityStrategy ?? 'HASH',
        p_identity_fields: input.identityFields ?? [],
        p_params: (input.params ?? {}) as never,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Dataset salvo com sucesso');
    },
    onError: (error: Error) => {
      logger.error('Falha ao salvar dataset', { message: error.message });
      toast.error(error.message || 'Não foi possível salvar o dataset');
    },
  });

  /** Exclui o dataset (dados são removidos fisicamente em segundo plano) */
  const deleteDataset = useMutation({
    mutationFn: async (datasetId: string) => {
      const { error } = await supabase.rpc('delete_external_dataset', { p_dataset_id: datasetId });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Dataset excluído');
    },
    onError: (error: Error) => {
      logger.error('Falha ao excluir dataset', { message: error.message });
      toast.error(error.message || 'Não foi possível excluir o dataset');
    },
  });

  /** Enfileira uma nova importação (o worker processa em segundo plano) */
  const startImport = useMutation({
    mutationFn: async (datasetId: string): Promise<string> => {
      const { data, error } = await supabase.rpc('start_external_import', {
        p_dataset_id: datasetId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Importação iniciada — acompanhe no histórico');
    },
    onError: (error: Error) => {
      logger.error('Falha ao iniciar importação', { message: error.message });
      toast.error(error.message || 'Não foi possível iniciar a importação');
    },
  });

  /** Cancela a importação em andamento */
  const cancelImport = useMutation({
    mutationFn: async (runId: string) => {
      const { error } = await supabase.rpc('cancel_external_import', { p_run_id: runId });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Importação cancelada');
    },
    onError: (error: Error) => {
      logger.error('Falha ao cancelar importação', { message: error.message });
      toast.error(error.message || 'Não foi possível cancelar a importação');
    },
  });

  /** Limpa os dados do dataset (troca de geração — instantâneo) */
  const clearData = useMutation({
    mutationFn: async (datasetId: string) => {
      const { error } = await supabase.rpc('clear_external_dataset_data', {
        p_dataset_id: datasetId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Dados removidos — a limpeza física roda em segundo plano');
    },
    onError: (error: Error) => {
      logger.error('Falha ao limpar dados', { message: error.message });
      toast.error(error.message || 'Não foi possível limpar os dados');
    },
  });

  return {
    datasets: datasetsQuery.data ?? [],
    isLoading: datasetsQuery.isLoading,
    refetch: datasetsQuery.refetch,
    saveDataset,
    deleteDataset,
    startImport,
    cancelImport,
    clearData,
  };
}

// =============================================================================
// HOOK — CAMPOS DO DATASET
// =============================================================================

/** Campos descobertos do dataset + gestão de índices (máx. 6 por dataset) */
export function useExternalDatasetFields(datasetId: string | null) {
  const queryClient = useQueryClient();

  const fieldsQuery = useQuery({
    queryKey: ['external-dataset-fields', datasetId],
    enabled: Boolean(datasetId),
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ExternalDatasetField[]> => {
      const { data, error } = await supabase.rpc('list_external_dataset_fields', {
        p_dataset_id: datasetId as string,
      });
      if (error) throw error;
      return (data ?? []) as unknown as ExternalDatasetField[];
    },
  });

  /** Liga/desliga índice de expressão para o campo (performance de filtros) */
  const setFieldIndexed = useMutation({
    mutationFn: async (input: { fieldId: string; isIndexed: boolean }) => {
      const { error } = await supabase.rpc('set_external_dataset_field_indexed', {
        p_field_id: input.fieldId,
        p_is_indexed: input.isIndexed,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-dataset-fields', datasetId] });
      toast.success('Índice atualizado');
    },
    onError: (error: Error) => {
      logger.error('Falha ao atualizar índice', { message: error.message });
      toast.error(error.message || 'Não foi possível atualizar o índice');
    },
  });

  return {
    fields: fieldsQuery.data ?? [],
    isLoading: fieldsQuery.isLoading,
    setFieldIndexed,
  };
}

// =============================================================================
// HOOK — HISTÓRICO DE SINCRONIZAÇÕES
// =============================================================================

/** Histórico paginado de execuções de importação */
export function useExternalImportRuns(options: {
  datasetId?: string | null;
  page?: number;
  pageSize?: number;
} = {}) {
  const { datasetId = null, page = 0, pageSize = 20 } = options;

  const runsQuery = useQuery({
    queryKey: ['external-import-runs', datasetId, page, pageSize],
    staleTime: 10 * 1000,
    queryFn: async (): Promise<ExternalImportRun[]> => {
      const { data, error } = await supabase.rpc('list_external_import_runs', {
        p_dataset_id: datasetId,
        p_limit: pageSize,
        p_offset: page * pageSize,
      });
      if (error) throw error;
      return (data ?? []) as unknown as ExternalImportRun[];
    },
    // Enquanto houver execução ativa, atualiza a cada 10s
    refetchInterval: (query) => {
      const rows = (query.state.data ?? []) as ExternalImportRun[];
      return rows.some((r) => r.status === 'running' || r.status === 'queued') ? 10_000 : false;
    },
  });

  const runs = runsQuery.data ?? [];

  return {
    runs,
    totalCount: runs[0]?.total_count ?? 0,
    isLoading: runsQuery.isLoading,
    refetch: runsQuery.refetch,
  };
}
